import { useSession } from '@sunucupark/client-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { closeAllComposers } from '@/features/compose';
import { usePreferences } from './state/preferences';

/**
 * Sends the reader to the sign-in page the moment a session ends — a revoked
 * session, a refresh token the gateway refused, or a sign-out in another tab.
 * The route guards only run on navigation, so without this the window would
 * keep showing a mailbox it can no longer load. What the account searched for,
 * the mail held in memory and the open composer windows go with it: the next
 * person at a shared computer must not see them.
 */
export function SessionWatcher() {
  const session = useSession();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const hadSession = useRef(session !== null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (session) {
      hadSession.current = true;
      return;
    }
    if (hadSession.current) {
      usePreferences.getState().forgetSearches();
      closeAllComposers();
      queryClient.clear();
    }
    if (!hadSession.current || pathname.startsWith('/sign-in')) return;
    hadSession.current = false;
    void navigate({ to: '/sign-in', search: { next: pathname } });
  }, [navigate, pathname, queryClient, session]);

  return null;
}
